import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ChevronDown, Menu, X } from "lucide-react";
import { BUSINESS } from "../../config/business";
import { SERVICES } from "../../config/content";

const NavItem = ({ to, children, testid }) => {
  const loc = useLocation();
  const active = loc.pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`nav-underline px-1 py-1 font-head text-sm font-semibold whitespace-nowrap transition-colors ${
        active ? "text-volt" : "text-ink hover:text-volt"
      }`}
    >
      {children}
    </Link>
  );
};

const Dropdown = ({ label, items, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={testid}
        className="nav-underline inline-flex items-center gap-1 px-1 py-1 font-head text-sm font-semibold text-ink hover:text-volt whitespace-nowrap"
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
          >
            <div className="w-64 rounded-2xl bg-white border border-line shadow-xl p-2">
              {items.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-drop-${it.slug}`}
                  className="block rounded-xl px-3 py-2 text-sm font-body text-ink hover:bg-volt-dim hover:text-volt transition-colors"
                >
                  {it.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const serviceItems = SERVICES.map((s) => ({ to: `/services/${s.slug}`, label: s.title, slug: s.slug }));
  const smogItems = SERVICES.filter((s) => s.slug.includes("smog")).map((s) => ({
    to: `/services/${s.slug}`,
    label: s.title,
    slug: s.slug,
  }));

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-line" data-testid="navbar">
      <div className="section flex items-center justify-between gap-4 py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src="/logo.webp" alt="Smog Hut Modesto — Smog and Registration" className="h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          <NavItem to="/" testid="nav-home">Home</NavItem>
          <Dropdown label="Services" items={serviceItems} testid="nav-services" />
          <Dropdown label="Smog Check" items={smogItems} testid="nav-smog" />
          <NavItem to="/pricing" testid="nav-pricing">Pricing</NavItem>
          <NavItem to="/coupons" testid="nav-coupons">Coupons</NavItem>
          <NavItem to="/locations/modesto" testid="nav-locations">Locations</NavItem>
          <NavItem to="/contact" testid="nav-contact">Contact</NavItem>
        </nav>

        <div className="flex items-center gap-2">
          <a href={`tel:${BUSINESS.phoneTel}`} data-testid="nav-call-btn" className="btn-volt !px-4 !py-2 hidden sm:inline-flex">
            <Phone className="h-4 w-4" /> Call Now
          </a>
          <button
            data-testid="nav-hamburger"
            className="lg:hidden grid h-10 w-10 place-items-center rounded-xl border border-line text-ink"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-line bg-white"
            data-testid="mobile-menu"
          >
            <div className="section py-4 flex flex-col gap-1">
              {[
                { to: "/", label: "Home" },
                { to: "/services", label: "Services" },
                { to: "/pricing", label: "Pricing" },
                { to: "/coupons", label: "Coupons" },
                { to: "/locations/modesto", label: "Locations" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="rounded-xl px-3 py-2.5 font-head font-semibold text-ink hover:bg-volt-dim hover:text-volt"
                >
                  {l.label}
                </Link>
              ))}
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-volt mt-2 w-full">
                <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
